/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css } from 'aphrodite';
import { isEmpty, compact } from 'lodash';
import { Text } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { DocumentsCard } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard';
import { DocumentTitles } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import { cardWrapperStyles } from 'blb-components/AuthenticatedApp/DashboardPage/SectionCard';
import { DashboardSection } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSection';
import { ATTORNEY_VIEWABLE_DOCUMENT_TYPES } from 'blb-constants/DocumentTypes';
import { Route } from 'blb-components/AuthenticatedApp/routeConfiguration';

import * as docUtils from '../common/utils/documents';
import {
  DEFAULT_LANDING_PATH,
  getSupportContactInfoForPrincipal,
  renderDefaultProfileSidebar,
  SupportContact,
} from '../common';
import { MeContext } from 'blb-contexts/Me';
import { FilterParams, OpsDeskFilterParams } from 'blb-constants/pipelineFilter';
import { Pipeline } from '@blend/search-client';
import { DashboardSectionItem } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSectionItem';

export { getClosingTypeCopy, getProfileTabs, getPipelinePollingInterval, getDashboardBanner } from '../common';

export const getSupportContactInfo = (): SupportContact => getSupportContactInfoForPrincipal('attorney');

const getDocumentSectionComponents = (
  viewableClosingDocs: BlendBridge.API.Document[],
  auditEvents?: EClose.FullClosing['auditEvents'],
): { receivedDocComponent: JSX.Element; uploadedDocComponent?: JSX.Element } => {
  const lenderClosingDocs = docUtils.filterDocsBySources(viewableClosingDocs, ['user']);
  const titleClosingDocs = docUtils.filterDocsBySources(viewableClosingDocs, ['settlement']);
  return {
    receivedDocComponent: (
      <div className={css(cardWrapperStyles.cardGroup)}>
        {!isEmpty(lenderClosingDocs) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              auditEvents={auditEvents}
              documents={lenderClosingDocs}
              id="it-LenderClosingDocuments"
              isUploaded={false}
              key="it-LenderClosingDocuments"
              title={DocumentTitles.LENDER_CLOSING_DOCUMENTS}
            />
          </div>
        )}
        {!isEmpty(titleClosingDocs) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              auditEvents={auditEvents}
              documents={titleClosingDocs}
              id="it-TitleClosingDocuments"
              isUploaded={false}
              key="it-TitleClosingDocuments"
              title="Title Documents"
            />
          </div>
        )}
      </div>
    ),
  };
};

// exported for tests only
export const getDocumentSectionConfigs = (
  me: BlendBridge.API.User,
  loan: BlendBridge.API.Loan,
  loanDocuments: BlendBridge.API.Document[],
): DashboardSection.Props['sectionConfigs'] => {
  const closingType = loan.closing?.ronSelectionData?.derivedClosingType || loan.closing?.closingType;
  const auditEvents = loan.closing?.auditEvents;
  const allClosingDocumentReferences = docUtils.getAllClosingDocumentReferences(
    loan.closing?.documentReferences,
    auditEvents,
  );
  const docIdToRefMap = docUtils.convertDocumentReferencesToDocIdToRefMap(allClosingDocumentReferences);
  const receivedDocs = docUtils.getReceivedClosingDocuments(me, loanDocuments);
  const { receivedDocComponent } = (() => {
    switch (closingType) {
      case 'HYBRID': {
        const dayOfDocs = docUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['DAY_OF']);
        const signAheadDocs = docUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['SIGN_AHEAD']);
        const titleClosingDocs = docUtils.filterDocsByTypes(receivedDocs, [
          'TITLE_DOCUMENT',
          'ADDITIONAL_CLOSING_DOCUMENT',
        ]);
        const viewableClosingDocs = compact([...dayOfDocs, ...signAheadDocs, ...titleClosingDocs]);
        return getDocumentSectionComponents(viewableClosingDocs, auditEvents);
      }
      case 'TRADITIONAL':
      case 'RON':
      default: {
        const viewableClosingDocs = docUtils.filterDocsByTypes(receivedDocs, ATTORNEY_VIEWABLE_DOCUMENT_TYPES);
        return getDocumentSectionComponents(viewableClosingDocs, auditEvents);
      }
    }
  })();

  return [
    {
      content: isEmpty(receivedDocs) ? <Text>No documents yet.</Text> : receivedDocComponent,
      title: 'Received Documents',
    },
  ];
};

export const renderDashboardOverview = (
  dashboardSections: DashboardSectionItem.SectionConfig[],
  loan: BlendBridge.API.Loan,
  documents: BlendBridge.API.Document[],
): React.ReactElement => (
  <>
    <DashboardSection sectionConfigs={dashboardSections} sectionHeader="Overview" />
    <DashboardSection
      sectionConfigs={getDocumentSectionConfigs(React.useContext(MeContext)!, loan, documents)}
      sectionHeader="Documents"
    />
  </>
);

export const renderProfileSidebar = renderDefaultProfileSidebar;

export const showSpecialInstructions = (): boolean => false;

export const getAllowedRoutes = (_me: BlendBridge.API.User): { allowedRoutes: Route[]; defaultLandingPath: Route } => {
  return {
    allowedRoutes: ['/:loanId/dashboard', '/pipeline'],
    defaultLandingPath: DEFAULT_LANDING_PATH,
  };
};

export const getPipelineFilters = (_filterParams: FilterParams): Pipeline.IFilter[] => {
  return [];
};

export const renderPipelineFilter = (
  _filters: OpsDeskFilterParams,
  _onFilterChanged: (filterParams: FilterParams) => void,
) => undefined;
